import { fetch } from 'whatwg-fetch';
import { ApiKey, ApiUrl, InputValidationKey, WebUrl, isSideNavCategory, isBottomNavCategory } from './Constant';
import moment from 'moment';
import { isEmpty } from 'lodash';

/// <summary>
/// Author : 
/// To Check if it is empty or null string
/// </summary>
export function stringIsNullOrEmpty(data) {
    return data == null || data == '';
}

/// Bank account holder name: stored UserFullName as-is. first/last only if UserFullName is empty.
export function getMemberAccountHolderName(member) {
    if (!member) {
        return "";
    }

    const fullName = (member.userFullName || "").trim();
    if (!stringIsNullOrEmpty(fullName)) {
        return fullName;
    }

    const surname = (member.lastName || "").trim();
    const givenName = (member.firstName || "").trim();
    return [surname, givenName].filter(Boolean).join(" ");
}

export function buildReferralRegisterUrl(referralCode, _languageCode = "en") {
    if (stringIsNullOrEmpty(referralCode)) {
        return "";
    }

    // WebUrl._URL_* already prefixes the current locale via Proxy — do not add lang again
    return `${window.location.origin}${WebUrl._URL_REGISTER}?code=${encodeURIComponent(referralCode)}`;
}

export function buildReferralQrUrl(referralCode, _languageCode = "en") {
    if (stringIsNullOrEmpty(referralCode)) {
        return "";
    }

    // WebUrl._URL_* already prefixes the current locale via Proxy — do not add lang again
    return `${window.location.origin}${WebUrl._URL_REFERRAL_QR_SCAN}${encodeURIComponent(referralCode)}`;
}

export const NAV_TITLE_MAX_LENGTH = 20;

export function truncateNavTitle(text, maxLength = NAV_TITLE_MAX_LENGTH) {
    if (stringIsNullOrEmpty(text)) return '';
    const str = String(text);
    if (str.length <= maxLength) return str;
    return `${str.slice(0, maxLength - 3)}...`;
}

export function resolveNavLabel(name, t) {
    const full = name
        ? (typeof t === 'function' ? (t(name) || name) : String(name))
        : '';
    return { full, display: truncateNavTitle(full) };
}

/** Unique nav item key — iconCategory + id avoids collisions across IconSetting and CompanyCustomNav tables. */
export function getNavItemRef(item) {
    if (!item) return null;
    const category = item.iconCategory ?? item['iconCategory'];
    const id = item.id ?? item['id'];
    if (category != null && id != null) return `${category}__${id}`;
    return item.iconName ?? item['iconName'] ?? null;
}

/** True for CompanyCustomNav-backed side/bottom items (not system side_nav / bottom_nav). */
export function isCustomNavItem(item) {
    if (!item) return false;
    const cat = item.iconCategory ?? item['iconCategory'];
    if (!cat) return false;
    if (cat === 'side_nav' || cat === 'bottom_nav') return false;
    return isSideNavCategory(cat) || isBottomNavCategory(cat);
}

/**
 * Guest + custom nav with isLoggedInRequired → caller should open login modal and stop.
 * Missing/undefined flag treated as false.
 */
export function requiresLoginForCustomNav(item, isLoggedIn) {
    if (isLoggedIn) return false;
    if (!isCustomNavItem(item)) return false;
    const required = item.isLoggedInRequired ?? item['isLoggedInRequired'];
    return required === true || required === 'true' || required === 1;
}

/** True when reference was wrongly set to a composite nav key (iconCategory__id). */
export function isNavCompositeRef(value) {
    return typeof value === 'string' && /^[a-z_]+__\d+$/i.test(value);
}

export function stripLocaleFromPath(pathname, locale = 'en') {
    if (!pathname) return '/';
    const normalized = normalizeNavPath(pathname);
    const regex = new RegExp(`^/${locale}(/|$)`);
    const stripped = normalized.replace(regex, '/');
    return stripped === '' ? '/' : normalizeNavPath(stripped);
}

/** Canonical game category — pathname match to system nav wins over display title (custom "sports" → "Sports"). */
export function resolveGameCategoryFromNavItem(item, companyIcons, locale = 'en') {
    const fallback = item?.iconName ?? item?.['iconName'] ?? item?.state ?? item?.['state'] ?? null;
    if (!item || !companyIcons?.length) return fallback;

    const itemPath = stripLocaleFromPath(item.pathname ?? item['pathname'] ?? '', locale);
    if (!itemPath || itemPath === '/' || /^https?:\/\//i.test(itemPath)) return fallback;

    const systemMatch = companyIcons.find(
        (i) =>
            (i.iconCategory === 'side_nav' || i.iconCategory === 'bottom_nav') &&
            normalizeNavPath(i.pathname) === itemPath
    );
    if (systemMatch?.iconName) return systemMatch.iconName;

    const pathMatch = companyIcons.find((i) => normalizeNavPath(i.pathname) === itemPath);
    if (pathMatch?.iconName) return pathMatch.iconName;

    return fallback;
}

/** GameList category from current route — prefers system nav iconName for the pathname. */
export function resolveGameCategoryReference(location, companyIcons, locale = 'en') {
    const locationPathname = stripLocaleFromPath(location?.pathname, locale);

    const systemMatch = (companyIcons || []).find(
        (i) =>
            (i.iconCategory === 'side_nav' || i.iconCategory === 'bottom_nav') &&
            normalizeNavPath(i.pathname) === locationPathname
    );
    if (systemMatch?.iconName) return systemMatch.iconName;

    const pathMatch = (companyIcons || []).find(
        (i) => normalizeNavPath(i.pathname) === locationPathname
    );
    if (pathMatch?.iconName) return pathMatch.iconName;

    const routeRef = isNavCompositeRef(location?.state?.reference)
        ? null
        : location?.state?.reference;
    if (!stringIsNullOrEmpty(routeRef)) return routeRef;

    return 'All';
}

/** Route state for nav clicks: reference = page category (GameList etc.), navRef = unique active key. */
export function getNavPushState(item, companyIcons, locale = 'en') {
    if (!item) return undefined;
    const reference = resolveGameCategoryFromNavItem(item, companyIcons, locale);
    const navRef = getNavItemRef(item);
    const state = {};
    if (reference != null && reference !== '') state.reference = reference;
    if (navRef != null && navRef !== '') state.navRef = navRef;
    return Object.keys(state).length ? state : undefined;
}

/**
 * Push/replace route with nav state. Replace when pathname+search unchanged so navRef
 * updates when two items share the same path (e.g. system Sports vs custom Sports).
 */
/** Only keep query string when explicitly provided (e.g. embedded in pathname). */
function resolveNavItemSearch(location, targetPathname, explicitSearch) {
    if (explicitSearch !== undefined) return explicitSearch;
    return '';
}

export function pushNavItemHistory(history, location, target) {
    // If pathname has an embedded query string (e.g. "/game-page?gameId=3874"), split it
    // so React Router can match the route correctly.
    let resolvedPathname = target.pathname || '';
    let embeddedSearch;
    const qIdx = resolvedPathname.indexOf('?');
    if (qIdx !== -1) {
        embeddedSearch = resolvedPathname.slice(qIdx);
        resolvedPathname = resolvedPathname.slice(0, qIdx);
    }

    const explicitOrEmbeddedSearch =
        target.search !== undefined
            ? target.search
            : embeddedSearch;

    const next = {
        pathname: resolvedPathname,
        state: target.state,
        search: resolveNavItemSearch(location, resolvedPathname, explicitOrEmbeddedSearch),
        hash: target.hash !== undefined ? target.hash : (location?.hash || ''),
    };
    const sameLocation =
        normalizeNavPath(location?.pathname) === normalizeNavPath(next.pathname) &&
        (location?.search || '') === (next.search || '') &&
        (location?.hash || '') === (next.hash || '');
    if (sameLocation) {
        history.replace(next);
    } else {
        history.push(next);
    }
}

export function navigateNavItem(history, location, pathname, item, companyIcons, locale = 'en') {
    pushNavItemHistory(history, location, {
        pathname,
        state: getNavPushState(item, companyIcons, locale),
    });
}

/** Resolve which bottom-nav tile index should appear selected. */
export function resolveBottomNavActiveIndex(bottomNavList, location) {
    if (!bottomNavList?.length) return 0;

    const pathname = normalizeNavPath(location?.pathname);
    const navRef = location?.state?.navRef;

    if (navRef) {
        const refIndex = bottomNavList.findIndex((item) => getNavItemRef(item) === navRef);
        if (refIndex >= 0) return refIndex;
    }

    const pathIndex = bottomNavList.findIndex((item) => {
        const itemPath = normalizeNavPath(item?.pathname ?? item?.['pathname'] ?? '');
        if (!itemPath || /^https?:\/\//i.test(itemPath)) return false;
        return itemPath === pathname;
    });
    if (pathIndex >= 0) return pathIndex;

    if (pathname.includes('/promo/') || pathname.endsWith('/promotion') || pathname.endsWith('/promo')) {
        const promoIndex = bottomNavList.findIndex((item) => {
            const itemPath = normalizeNavPath(item?.pathname ?? item?.['pathname'] ?? '');
            return itemPath.endsWith('/promotion') || itemPath.endsWith('/promo');
        });
        if (promoIndex >= 0) return promoIndex;
    }

    for (const suffix of ['/referral', '/livechat', '/settings']) {
        if (pathname.endsWith(suffix)) {
            const suffixIndex = bottomNavList.findIndex((item) =>
                normalizeNavPath(item?.pathname ?? item?.['pathname'] ?? '').endsWith(suffix)
            );
            if (suffixIndex >= 0) return suffixIndex;
        }
    }

    return 0;
}

export function normalizeNavPath(path) {
    if (!path) return '';
    return path.endsWith('/') && path.length > 1 ? path.slice(0, -1) : path;
}

/** Map deposit/withdraw nav targets to Settings finance tab id, if applicable. */
export function resolveSettingsFinanceTab(pathname, item) {
    const path = normalizeNavPath(pathname || '');
    const icon = (item?.iconName ?? item?.['iconName'] ?? '').trim().toLowerCase();
    const title = (item?.title ?? item?.text ?? '').trim().toLowerCase();

    if (
        path === WebUrl._URL_DEPOSIT ||
        path.endsWith('/deposit') ||
        icon === 'deposit' ||
        title === 'deposit'
    ) {
        return 'deposit';
    }

    if (
        path === WebUrl._URL_WITHDRAWAL ||
        path.endsWith('/withdrawal') ||
        path.endsWith('/withdraw') ||
        icon === 'withdrawal' ||
        icon === 'withdraw' ||
        title === 'withdrawal' ||
        title === 'withdraw'
    ) {
        return 'withdraw';
    }

    return null;
}

/** Navigate to Settings deposit/withdrawal sections (same ids as AccountSidebar). */
export function pushSettingsFinanceTab(history, financeTab) {
    const activeMenu = financeTab === 'withdraw' ? 'withdrawal' : 'deposit';
    history.push({
        pathname: WebUrl._URL_SETTINGS,
        state: { activeMenu },
    });
}

export function getNavItemPath(item, locale) {
    const raw = item?.pathname ?? item?.['pathname'] ?? '';
    if (!raw) return '';
    return locale ? `/${locale}${raw}` : raw;
}

export function isNavItemActive(item, activeRef) {
    if (!item || activeRef == null || activeRef === '') return false;
    return getNavItemRef(item) === activeRef;
}

/** Resolve which nav item should appear selected from route state or pathname (first pathname match only). */
export function resolveActiveNavRef(navItems, location, locale) {
    if (location?.state?.navRef) return location.state.navRef;

    const pathname = normalizeNavPath(location?.pathname);
    const homePath = locale ? `/${locale}` : '/';

    if (pathname === '/' || pathname === homePath) {
        const home = navItems?.find((i) => (i.iconName ?? i['iconName']) === 'Home');
        return home ? getNavItemRef(home) : null;
    }

    const match = navItems?.find(
        (item) => normalizeNavPath(getNavItemPath(item, locale)) === pathname
    );
    return match ? getNavItemRef(match) : null;
}

/// <summary>
/// Author : 
/// Convert array into form that use to post data
/// </summary>
export function createFormBody(params) {
    let formBody = [];

    for (let property in params) {
        let encodedKey = encodeURIComponent(property);
        let encodedValue = encodeURIComponent(params[property]);
        formBody.push(encodedKey + "=" + encodedValue);
    }

    formBody = formBody.join("&");
    return formBody;
}

/// <summary>
/// Author : 
/// Convert array into form that use to post data
/// </summary>
export function createMultiPartFormBody(params) {
    var formBody = new FormData();

    for (const [key, value] of Object.entries(params)) {
        formBody.append(key, value);
    }

    return formBody;
}

/// <summary>
/// Author : 
/// Used to Navigation to certain path
/// </summary>
export function navigateTo(history, url, state) {
    if (state !== undefined) {
        history.push({
            pathname: url,
            state: state
        });
    } else {
        history.push(url);
    }
}

/// <summary>
/// Author : 
/// </summary>
export const dataTableFilterCaseInsensitive = (filter, row) => {
    const id = filter.pivotId || filter.id;
    if (row[id] !== null && typeof row[id] === 'string') {
        return (
            row[id] !== undefined ?
            String(row[id].toLowerCase()).startsWith(filter.value.toLowerCase()) : true
        )
    } else {
        return (
            String(row[filter.id]) === filter.value
        )
    }
}

/// <summary>
/// Author: -
/// </summary>
export function getValidationMessage(error) {
    var validationMsg = "";
    switch (error.type) {
        case InputValidationKey._REQUIRED_KEY:
            validationMsg = "INPUT_FIELD_REQUIRED";
            break;
        case InputValidationKey._MAX_LENGTH_KEY:
            validationMsg = "MAX_LENGTH_EXCEEDED";
            break;
        case InputValidationKey._MIN_LENGTH_KEY:
            validationMsg = "MIN_LENGTH_NO_MET";
            break;
        default:
            validationMsg = error.message
            break;
    }
    return validationMsg;
}

/// <summary>
/// Author: -
/// </summary>
export const capitalize = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

/// <summary>
/// Author: -
/// </summary>
export function isObjectEmpty(data) {
    return Object.keys(data).length === 0;
}

/// <summary>
/// Author : 
/// </summary>
export async function imagePathToFileObject(imagePath) {
    const response = await fetch(imagePath);
    const blob = await response.blob();
    const file = new File([blob], imagePath);
    return file;
}

/// <summary>
/// Author : 
/// </summary>
export function formatNumber(data, decimalPlaces) {
    return parseFloat(data).toLocaleString(undefined, { minimumFractionDigits: decimalPlaces })
}

/// <summary>
/// Author : 
/// </summary>
export function arrayGroupByKey(array, key) {
    return array.reduce((result, currentValue) => {
        (result[currentValue[key]] = result[currentValue[key]] || []).push(
            currentValue
        );

        return result;
    }, {});
};

/// <summary>
/// Normalize to a decimal string without IEEE-754 scale errors (e.g. 16.08 stays "16.08").
/// </summary>
function toStableDecimalString(value, decimalPlaces) {
    if (value == null || value === '') {
        return '0';
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed !== '' && /^-?\d+(\.\d+)?$/.test(trimmed)) {
            return trimmed;
        }
    }
    const n = parseFloat(value);
    if (isNaN(n)) {
        return '0';
    }
    const places = Math.max(0, decimalPlaces | 0);
    const scale = Math.pow(10, places + 4);
    const stabilized = Math.round(n * scale) / scale;
    return stabilized.toFixed(places + 4).replace(/\.?0+$/, '') || '0';
}

/// <summary>
/// Truncate toward zero at decimalPlaces (cutoff), not round-off.
/// </summary>
export function TruncateDecimal(value, decimalPlaces) {
    if (value == null || value === '' || isNaN(parseFloat(value))) {
        return 0;
    }
    const places = Math.max(0, decimalPlaces | 0);
    if (places === 0) {
        return Math.trunc(parseFloat(value));
    }
    return parseFloat(cutoffFixed(value, places));
}

/// <summary>
/// Format a number with cutoff decimals and fixed trailing zeros, without round-off.
/// </summary>
export function cutoffFixed(value, decimalPlaces) {
    if (value == null || value === '') {
        const places = Math.max(0, decimalPlaces | 0);
        return places === 0 ? '0' : `0.${'0'.repeat(places)}`;
    }

    const n = parseFloat(value);
    if (isNaN(n)) {
        const places = Math.max(0, decimalPlaces | 0);
        return places === 0 ? '0' : `0.${'0'.repeat(places)}`;
    }

    const places = Math.max(0, decimalPlaces | 0);
    if (places === 0) {
        return Math.trunc(n).toString();
    }

    const numericStr = toStableDecimalString(value, places);
    const negative = numericStr.startsWith('-');
    const unsigned = negative ? numericStr.slice(1) : numericStr;
    const [intPart = '0', fracPart = ''] = unsigned.split('.');
    const truncatedFrac = (fracPart + '0'.repeat(places)).slice(0, places);

    return `${negative ? '-' : ''}${intPart}.${truncatedFrac}`;
}

/// <summary>
/// Return a numeric value after applying cutoff decimals.
/// </summary>
export function cutoffNumber(value, decimalPlaces) {
    return parseFloat(cutoffFixed(value, decimalPlaces));
}

/// <summary>
/// Author : 
/// </summary>
export function numberWithCurrencyFormat(value, decimal = 2, force = false) {
    const CURRENCY_DECIMAL = 2;

    if (value == null || value === '' || isNaN(value)) {
        value = 0;
    }
    
    if (decimal === 0) {
        // replace round-off (Math.Round) with cutoff — 2026/04/07
        return TruncateDecimal(value, 0).toString().replace(/\d(?=(\d{3})+$)/g, '$&,');
    }


    // replace round-off (Math.Round) with cutoff — 2026/04/07s
    var convertedNumber = cutoffFixed(value, decimal).toString().replace(/\d(?=(\d{3})+\.)/g, '$&,');

    if (decimal > CURRENCY_DECIMAL) {
        let afterDecimalNumber = convertedNumber.split('.');
        let decimalDiff = decimal - CURRENCY_DECIMAL;
        if (afterDecimalNumber.length == 2 && convertedNumber.lastIndexOf('0') == (convertedNumber.length - 1)) {
            let processNumber = afterDecimalNumber[1];
            let beginNumber = !force ? processNumber.length - 1 : processNumber;
            for (let i = beginNumber; (i > 0 && decimalDiff > 0); i--) {
                if (processNumber.charAt(i) == '0') {
                    processNumber = processNumber.substr(0, i) + processNumber.substr(i + 1);
                } else {
                    break;
                }
                decimalDiff--;
            }
            convertedNumber = afterDecimalNumber[0] + "." + processNumber;
        }
    }

    return convertedNumber
}

/// <summary>
/// Author : 
/// </summary>
export function ignoreDecimal(e) {
    const _KEYCODE_DECIMAL = 190;
    const _KEYCODE_E = 69;
    let keyCode = e.keyCode;

    if (keyCode == _KEYCODE_DECIMAL || keyCode == _KEYCODE_E) {
        e.preventDefault();
    }
}

export function formatPercent(v) {
    return `${v}%`
}

// CHRIS_LOCALE: Add locale definitions and functions
const VALID_LOCALES =  [ 'ab' , 'aa' , 'af' , 'ak' , 'sq' , 'am' , 'ar' , 'an' , 'hy' , 'as' , 'av' , 'ae' , 'ay' , 'az' , 'bm' , 'ba' , 'eu' , 'be' , 'bn' , 'bh' , 'bi' , 'bs' , 'br' , 'bg' , 'my' , 'ca' , 'km' , 'ch' , 'ce' , 'ny' , 'zh' , 'cu' , 'cv' , 'kw' , 'co' , 'cr' , 'hr' , 'cs' , 'da' , 'dv' , 'nl' , 'dz' , 'en' , 'eo' , 'et' , 'ee' , 'fo' , 'fj' , 'fi' , 'fr' , 'ff' , 'gd' , 'gl' , 'lg' , 'ka' , 'de' , 'ki' , 'el' , 'kl' , 'gn' , 'gu' , 'ht' , 'ha' , 'he' , 'hz' , 'hi' , 'ho' , 'hu' , 'is' , 'io' , 'ig' , 'id' , 'ia' , 'ie' , 'iu' , 'ik' , 'ga' , 'it' , 'ja' , 'jv' , 'kn' , 'kr' , 'ks' , 'kk' , 'rw' , 'kv' , 'kg' , 'ko' , 'kj' , 'ku' , 'ky' , 'lo' , 'la' , 'lv' , 'lb' , 'li' , 'ln' , 'lt' , 'lu' , 'mk' , 'mg' , 'ms' , 'ml' , 'mt' , 'gv' , 'mi' , 'mr' , 'mh' , 'ro' , 'mn' , 'na' , 'nv' , 'nd' , 'ng' , 'ne' , 'se' , 'no' , 'nb' , 'nn' , 'ii' , 'oc' , 'oj' , 'or' , 'om' , 'os' , 'pi' , 'pa' , 'ps' , 'fa' , 'pl' , 'pt' , 'qu' , 'rm' , 'rn' , 'ru' , 'sm' , 'sg' , 'sa' , 'sc' , 'sr' , 'sn' , 'sd' , 'si' , 'sk' , 'sl' , 'so' , 'st' , 'nr' , 'es' , 'su' , 'sw' , 'ss' , 'sv' , 'tl' , 'ty' , 'tg' , 'ta' , 'tt' , 'te' , 'th' , 'bo' , 'ti' , 'to' , 'ts' , 'tn' , 'tr' , 'tk' , 'tw' , 'ug' , 'uk' , 'ur' , 'uz' , 've' , 'vi' , 'vo' , 'wa' , 'cy' , 'fy' , 'wo' , 'xh' , 'yi' , 'yo' , 'za' , 'zu'];  // ISO 639-1 only
const NON_STANDARD_LOCALES = ['tpi', 'zk', 'kh'];  // known internal extras

export function isValidLocale(locale) {
    const localeExists = VALID_LOCALES.includes(locale) || NON_STANDARD_LOCALES.includes(locale);
    if (!localeExists) {
        //console.warn(`Locale ${locale} is not valid. Check VALID_LOCALES in Util.js`);
    }
    return localeExists;
}

// Checks if url has valid locale
export function hasValidLocale(pathname) {
    // Get locale from browser url
    const browserLocale = extractLocaleFromPathname(pathname);

    // For condition where url ends with locale (eg. /en)
    const regex = new RegExp(`^/${browserLocale}$`);
    if(isValidLocale(browserLocale) && pathname.endsWith(`/${browserLocale}`) && pathname.match(regex)) {
        return `${browserLocale}`
    }

    // For condition where url contains locale (eg. /en/login)
    const regex2 = new RegExp(`^/${browserLocale}(/|$)`)
    if(isValidLocale(browserLocale) && pathname.startsWith(`/${browserLocale}`) && pathname.match(regex2)) {
        return `${browserLocale}`
    } else {
        return false
    }
}

export function extractLocaleFromPathname(pathname) {
    // Split the pathname by slashes and filter out empty strings
    const pathSegments = pathname.split('/').filter(segment => segment.trim() !== '');
    // Check if there's at least one segment that could be the locale
    if (pathSegments.length > 0) {
        return pathSegments[0];
    } else {
        return null; // or a default locale, depending on your application's needs
    }
}

export function abbreviateNumber(number) {
    //parse the number just incase got "," or any symbols - john
    const parsedNumber = parseInt(number.replace(/[^0-9.-]+/g, ""));

    if (parsedNumber >= 1_000_000_000) {
      return (parsedNumber / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'b';
    } else if (parsedNumber >= 1_000_000) {
      return (parsedNumber / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'm';
    } else if (parsedNumber >= 1_000) {
      return (parsedNumber / 1_000).toFixed(1).replace(/\.0$/, '') + 'k';
    }
    return parsedNumber.toString();
}

// Utility function to adjust the font size
export const adjustFontSizeToFit = () => {
    // Select all the elements you want to adjust (you can customize this selector)
    const elements = document.querySelectorAll(".adjust-font-size");

    elements.forEach(element => {
        //console.log(element);
        const maxWidth = element.clientWidth;
        const maxHeight = element.clientHeight;
        let fontSize = parseInt(window.getComputedStyle(element).fontSize, 10);
        //console.log("element.scrollWidth", element.scrollWidth, " > ", maxWidth, " | ", element.scrollHeight , " > " , maxHeight)

        // Decrease font size until the element fits within the container
        while (
            (element.scrollWidth > maxWidth || element.scrollHeight > maxHeight) &&
            fontSize > 10
        ) {
            //console.log(fontSize)
            fontSize -= 1;
            element.style.fontSize = `${fontSize}px`;
        }
    });
};

export { pushGtmSignUp, pushGtmPurchase } from "../components/GoogleTag/ReferralGtm.js";

export function waitForFbPixel(callback, timeout = 5000) {
    const startTime = Date.now();

    function checkFbq() {
        // If fbq is defined, loaded, and at least one pixel is configured, execute callback.
        if (window.fbq !== undefined && window.fbq?.loaded) {
            // console.log("FB Pixel loaded");
            callback();
        }
        // If timeout exceeded, log a warning and stop checking.
        else if (Date.now() - startTime >= timeout) {
            //console.warn("FB Pixel not loaded within timeout");
        }
        // Otherwise, check again after 200ms.
        else {
            setTimeout(checkFbq, 200);
        }
    }

    checkFbq();
}

export function convertToSiteTime(date, offset = 0, format = "YYYY-MM-DD HH:mm:ss") {
    if (!date) {
        return "";
    }

    // Ensure the input date is treated as UTC
    const newDate = moment.utc(date).add(offset, 'hours');

    // Format the converted date
    return newDate.format(format);
}

export function checkHotGamesField(data, userData) {
    if (isEmpty(userData)) return data;
  
    var result= data.map((game) => {
      var isPromotion = true;
      const freeGGRDisable = userData.isFreeGGRPromo;

      if(userData.isActivedPromoExist){ 
      isPromotion=false;
      if(userData.promotionScope==1){
        isPromotion = true;
      }else if(userData.promotionScope==2 && userData.promotionScopeCategories.includes(game.categoryId)){
        isPromotion=true;
      }else if(userData.promotionScope==3 && userData.promotionScopeProducts.includes(game.providerId)){
        isPromotion=true;
      }
      }

      return {
        ...game,
        isPromotion,
        freeGGRDisable,
      };
    });

    return result;
  }

export function getMembershipRebateRate(category, memberData) {
    let rebatePercentage = 0;

    switch (category) {
        case "Slots":
            rebatePercentage = memberData.membership?.rebateSlot ?? 0;
            break;
        case "Sports":
            rebatePercentage = memberData.membership?.rebateSport ?? 0;
            break;
        case "ESport":
            rebatePercentage = memberData.membership?.rebateESport ?? 0;
            break;
        case "Casino":
            rebatePercentage = memberData.membership?.rebateCasino ?? 0;
            break;
        case "Fish Hunt":
            rebatePercentage = memberData.membership?.rebateFishHunt ?? 0;
            break;
        case "Keno":
            rebatePercentage = memberData.membership?.rebateKeno ?? 0;
            break;
        case "Arcade":
            rebatePercentage = memberData.membership?.rebateArcade ?? 0;
            break;
        case "Crash":
            rebatePercentage = memberData.membership?.rebateCrash ?? 0;
            break;
        case "Exchange":
            rebatePercentage = memberData.membership?.rebateExchange ?? 0;
            break;
        case "RNG":
            rebatePercentage = memberData.membership?.rebateRNG ?? 0;
            break;
        case "Poker":
            rebatePercentage = memberData.membership?.rebatePoker ?? 0;
            break;
        default:
            rebatePercentage = 0.00;
    }

    return rebatePercentage.toFixed(2);
}

// Stay under Cloudflare's ~60s origin timeout. TAC is saved before SMS send.
export const GENERATE_OTP_CLIENT_TIMEOUT_MS = 20000;

const OTP_SMS_DELIVERY_FAILURE_STATUSES = new Set([0, 408, 502, 503, 504]);

/** Gateway/timeout while sending SMS. OTP row is already persisted — do not block the OTP screen. */
export function isOtpSmsDeliveryFailure(responseJson) {
    if (!responseJson || responseJson.success) {
        return false;
    }
    const status = Number(responseJson.status);
    if (OTP_SMS_DELIVERY_FAILURE_STATUSES.has(status)) {
        return true;
    }
    const msg = String(responseJson.message || "");
    if (/cloudflare|gateway timeout|did not respond to Cloudflare|origin web server/i.test(msg)) {
        return true;
    }
    if (!Number.isFinite(status)) {
        return !msg || /timeout|network|ECONNABORTED|PLEASE_TRY_AGAIN/i.test(msg);
    }
    return false;
}

export function formatApiMessage(t, raw, fallbackKey = 'UNKNOWN_ERROR') {
  if (!raw) {
    return t(fallbackKey);
  }

  // 1) If it’s already an object with { key|message, parameters }
  if (typeof raw === 'object') {
    const key = raw.message || raw.key;
    const params = raw.parameters || raw.params || {};
    if (typeof key === 'string') {
      return t(key, params);
    }
    return t(fallbackKey);
  }

  // 2) If it’s a string that looks like JSON, try to parse
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        const parsed = JSON.parse(trimmed);
        const key = parsed.message || parsed.key;
        const params = parsed.parameters || parsed.params || {};
        if (typeof key === 'string') {
          return t(key, params);
        }
        // fallback if parsed but missing key
        return t(fallbackKey);
      } catch (err) {
        // in dev, you might want to log:
        if (process.env.NODE_ENV !== 'production') {
          //console.warn('formatApiMessage: invalid JSON payload:', raw, err);
        }
        // fall through to plain‐key case
      }
    }

    // 3) Plain translation key
    return t(trimmed);
  }

  // 4) Anything else → fallback
  return t(fallbackKey);
}

export function CheckIsRTPValueExist(game) {

    var rtpValue = null;
    var isRtpUp = null;

    const rtpData = game.fixedRTPByUser
        ? (() => {
            try {
                return JSON.parse(game.fixedRTPByUser);
            } catch {
                return null;
            }
        })()
        : null;

    if (rtpData?.NewRTPPercentage != null) {
        rtpValue = rtpData.NewRTPPercentage;
        isRtpUp = rtpData.NewRTPPercentage >= rtpData.OldRTPPercentage;
    } else if (game?.newRTPPercentage != null) {
        rtpValue = game.newRTPPercentage;
        isRtpUp = game.newRTPPercentage >= game.oldRTPPercentage;
    }

    return { rtpValue, isRtpUp };
}

/// <summary>
/// Validates SVG file content to prevent XSS attacks
/// Author : -
/// </summary>
export async function validateSvgFile(file) {
  if (!file) {
    throw new Error("SVG_FILE_INVALID");
  }

  // Check if file is SVG by extension or MIME type
  const fileName = file.name || '';
  const fileExtension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
  const isSvg = fileExtension === '.svg' || file.type === 'image/svg+xml';

  if (!isSvg) {
    return true; // Not an SVG file, no validation needed
  }

  // Read file content as text
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const svgContent = e.target.result;
        
        if (!svgContent || typeof svgContent !== 'string') {
          reject(new Error("SVG_FILE_INVALID"));
          return;
        }

        // Normalize content to lowercase for case-insensitive matching
        const normalizedContent = svgContent.toLowerCase();

        // List of dangerous patterns that could be used for XSS attacks
        const dangerousPatterns = [
          // Script tags
          '<script',
          '</script>',
          
          // Event handlers (common XSS vectors)
          'onload=',
          'onerror=',
          'onclick=',
          'onmouseover=',
          'onfocus=',
          'onblur=',
          'onchange=',
          'onsubmit=',
          'onreset=',
          'onselect=',
          'onunload=',
          'onabort=',
          'onkeydown=',
          'onkeypress=',
          'onkeyup=',
          'onmousedown=',
          'onmousemove=',
          'onmouseout=',
          'onmouseup=',
          
          // Dangerous elements
          '<foreignobject',
          '<foreignobject>',
          '<iframe',
          '<iframe>',
          '<embed',
          '<embed>',
          '<object',
          '<object>',
          
          // JavaScript protocol
          'javascript:',
          'data:text/html',
          'vbscript:',
          
          // Base64 encoded scripts (common pattern: data:image/svg+xml;base64,PHNjcmlwdD4=)
          'data:image/svg+xml;base64,',
        ];

        // Check for dangerous patterns
        for (const pattern of dangerousPatterns) {
          if (normalizedContent.includes(pattern)) {
            reject(new Error("SVG_FILE_INVALID"));
            return;
          }
        }

        // Additional check: Look for script tags with various encodings
        // Check for encoded script tags (e.g., &#60;script, &lt;script, etc.)
        const encodedScriptPattern = /(&#60;|&lt;|%3c|%3C)\s*script/i;
        if (encodedScriptPattern.test(normalizedContent)) {
          reject(new Error("SVG_FILE_INVALID"));
          return;
        }

        // Check for event handlers with encoded characters
        const eventHandlerPattern = /on\w+\s*=\s*['"]?\s*(javascript|data|vbscript):/i;
        if (eventHandlerPattern.test(normalizedContent)) {
          reject(new Error("SVG_FILE_INVALID"));
          return;
        }

        // Validate that it's a valid SVG structure (basic check)
        if (!normalizedContent.includes('<svg') && !normalizedContent.includes('<?xml')) {
          reject(new Error("SVG_FILE_INVALID"));
          return;
        }

        // Validation passed
        resolve(true);
      } catch (error) {
        reject(new Error("SVG_FILE_INVALID"));
      }
    };

    reader.onerror = () => {
      reject(new Error("SVG_FILE_INVALID"));
    };

    reader.readAsText(file);
  });
}

export const LOGIN_CAPTCHA_LENGTH = 4;

export function getLoginCaptchaFieldRules() {
  return {
    required: "PLEASE_ENTER_CAPTCHA",
    minLength: {
      value: LOGIN_CAPTCHA_LENGTH,
      message: "PLEASE_FOLLOW_CAPTCHA_LENGTH",
    },
    maxLength: {
      value: LOGIN_CAPTCHA_LENGTH,
      message: "PLEASE_FOLLOW_CAPTCHA_LENGTH",
    },
  };
}

export async function fetchLoginCaptchaStatus() {
  const ApiEngine = require("./ApiEngine").default;
  const responseJson = await ApiEngine.get(ApiUrl._API_GET_LOGIN_CAPTCHA_IP_STATUS);
  if (!responseJson || !responseJson[ApiKey._API_SUCCESS_KEY]) {
    return null;
  }
  const data = responseJson[ApiKey._API_DATA_KEY] || {};
  return {
    captchaRequired: !!data.captchaRequired,
    ipBanned: !!data.ipBanned,
  };
}

export async function fetchLoginCaptchaChallenge() {
  const ApiEngine = require("./ApiEngine").default;
  const responseJson = await ApiEngine.get(ApiUrl._API_GET_LOGIN_TICKET);
  const data = responseJson && responseJson[ApiKey._API_DATA_KEY];
  if (!data || !data.loginTicket || !data.captchaImage) {
    return null;
  }
  return {
    loginTicket: data.loginTicket,
    captchaImage: data.captchaImage,
  };
}
